/**
 * THE ONE PLACE A PHONE NUMBER IS SHAPED.
 *
 * The platform stores and configures exactly one form of a number: E.164
 * (`+12896775545`) — what the SIP carrier registers, what `tel:` links dial,
 * what `LIVEKIT_SIP_PHONE_NUMBER` carries. Every other form is DERIVED here,
 * at the point of use, and never stored.
 *
 * Why this file exists (owner, 2026-09-05): every overlay used to carry the
 * same number twice — once in E.164 and once hand-formatted for display in a
 * second variable — and the UI carried the formatter twice too, one copy per
 * component "to avoid a cross-component import for one tiny helper". Two
 * numbers that must agree is one number and a bug waiting for a DID change.
 *
 * NANP-only on purpose: every DID this platform has ever been issued is
 * +1, and a wrong guess at another country's grouping would be worse than
 * showing the E.164 as-is — which is what a non-NANP input gets.
 */

/** Digits only, no plus. `""` for an input with no digits. */
function digitsOf(input: string): string {
  return input.replace(/\D/g, '');
}

/**
 * Normalise anything a human or a config might type into E.164.
 *   "+1 (289) 677-5545" -> "+12896775545"
 *   "2896775545"        -> "+12896775545"   (bare NANP national number)
 *   "+442071234567"     -> "+442071234567"  (already E.164, left alone)
 * Returns `null` when there are no digits at all.
 */
export function toE164(input: string | null | undefined): string | null {
  if (!input) return null;
  const digits = digitsOf(input);
  if (!digits) return null;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
}

/**
 * THE MASK. E.164 -> what a person reads.
 *   "+12896775545" -> "+1 (289) 677-5545"
 * Idempotent: feeding it its own output returns the same string, so a caller
 * never has to know whether a value was formatted already. A number the mask
 * does not understand (non-NANP) is returned in E.164 rather than mangled.
 */
export function formatPhoneForDisplay(input: string | null | undefined): string {
  const e164 = toE164(input);
  if (!e164) return '';
  const digits = e164.slice(1);
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return e164;
}

/**
 * A `tel:` URI, with the PIN auto-dialed after the IVR answers.
 * Each `,` is roughly a two-second pause on iOS/Android; four reliably clears
 * the "enter PIN" prompt before the digits are sent. Desktop browsers still
 * offer the link (FaceTime / Phone Link / etc.).
 */
export function buildTelHref(input: string, pin?: string | null): string {
  const e164 = toE164(input) ?? digitsOf(input);
  const pinDigits = pin ? digitsOf(pin) : '';
  return pinDigits ? `tel:${e164},,,,${pinDigits}` : `tel:${e164}`;
}
